<template>
  <BaseModal
    :modal-class="'diet-assign-to-patient-modal'"
    @close="close"
    @confirm="assignToPatientConfirm"
    @click="hideAutocompleteOptions"
  >
    <template #title>
      <Space :space="2">
        {{ props.title || 'Skopiuj do pacjenta...' }}
        <T as="span" size="sm">Kopia trafi na listę jadłospisów pacjenta ze statusem Szkic.</T>
      </Space>
    </template>
    <div v-if="patients.length === 0" class="patients-list-empty">
      Aby skopiować jadłospis, musisz mieć co najmniej jednego aktywnego pacjenta.
    </div>
    <div v-else class="patient-list">
      <div class="patient-week">
        <div class="patient">
          <BaseAutocompleteVirtualized
            ref="autocomplete"
            v-model="patient"
            class="patient__select"
            :options="patients"
            :placeholder="'Wybierz pacjenta'"
            :debounce-after="300"
            dynamic-height
          >
            <template #option="{ option }">
              <div class="patient-option">
                <span class="patient-option__name">
                  {{ option.name }}
                  (wiek: {{ option.birthDate }}
                  )
                </span>
                <span class="patient-option__email">{{ option.email || 'brak adresu email' }}</span>
              </div>
            </template>
          </BaseAutocompleteVirtualized>
        </div>
        <Space v-if="patient?.hasMissingData" :space="4" align-y="center" direction="columns">
          <div class="badge">
            <BaseIcon name="error" class="icon-warning" />
            Brakujące dane (płeć i/lub wiek)
          </div>
          <StyledButton variant="outline" right-icon="open_in_new" @click="openPatientProfile">
            Otwórz profil pacjenta
          </StyledButton>
        </Space>
      </div>
    </div>
    <template #actions>
      <StyledButton variant="outline" @click.once="close">Anuluj</StyledButton>
      <StyledButton
        v-if="patients.length > 0"
        :disabled="assignToPatientDisabled"
        @click.once="assignToPatientConfirm"
      >
        Skopiuj
      </StyledButton>
      <StyledButton v-else @click.once="assignToPatientRedirect">
        Przejdź do zakładki "Pacjenci"
      </StyledButton>
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { differenceInYears, parse } from 'date-fns'
  import type { PropType } from 'vue'
  import { computed, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'

  import BaseAutocompleteVirtualized from '@/components/BaseAutocompleteVirtualized.vue'
  import BaseIcon from '@/components/BaseIcon.vue'
  import BaseModal from '@/components/BaseModal.vue'
  import { Space } from '@/components/Space'
  import { StyledButton } from '@/components/StyledButton'
  import { T } from '@/components/ui/T'
  import eventBus from '@/eventBus'
  import { dietsService } from '@/services/dietsService'
  import { useGlobalStore } from '@/store/globalStore'
  import { reportError } from '@/utils/reportError'

  import type { DietType } from '@/store/types'

  const props = defineProps({
    dietId: {
      required: true,
      type: Number,
    },
    dietType: {
      required: true,
      type: String as PropType<DietType>,
    },
    patientId: {
      required: false,
      type: Number,
    },
    title: {
      required: false,
      type: String,
    },
  })
  const globalStore = useGlobalStore()
  const router = useRouter()
  const sending = ref(false)
  type PatientOption = {
    birthDate: number | string
    hasMissingData: boolean
    email: string | null
    gender: number | null
    id?: number
    name: string
    text: string
    value: number
  }

  const patient = ref<PatientOption>()

  const user = computed(() => globalStore.user)
  const emits = defineEmits(['close', 'sendSystemDietsListToFile'])

  const mapPatientToOption = (v: {
    birth_date: string | null
    email: string | null
    gender: number | null
    id: number
    name: string | null
  }): PatientOption => {
    const name = v.name || 'Pacjent bez nazwy'

    const birthDate = v.birth_date
      ? differenceInYears(new Date(), parse(v.birth_date, 'yyyy-MM-dd', new Date()))
      : 'b.d.'

    return {
      birthDate,
      email: v.email,
      gender: v.gender,
      hasMissingData: !v.gender || !birthDate,
      id: v.id,
      name,
      text: `${name} ${v.email ? `(${v.email})` : '(brak adresu email)'}`,
      value: v.id,
    }
  }

  const patients = computed(() => {
    return (user.value?.patients ?? []).map(mapPatientToOption)
  })

  watch(
    () => [user.value?.patients, props.patientId] as const,
    ([patientsList, patientId]) => {
      if (!patientsList?.length || patientId == null) return
      const matched = patientsList.find((p: { id: number }) => p.id === patientId)
      if (matched) {
        patient.value = mapPatientToOption(matched)
      }
    },
    { immediate: true },
  )

  const assignToPatientDisabled = computed(() => {
    return sending.value || patient.value?.hasMissingData
  })

  const hideAutocompleteOptions = () => {
    eventBus.emit('hideAutocompleteOptions')
  }
  const close = () => {
    emits('close')
  }

  const assignToPatientConfirm = async () => {
    if (!assignToPatientDisabled.value && patient.value?.id) {
      try {
        if (props.dietType === 'system') {
          emits('sendSystemDietsListToFile', props.dietId)
        }
        sending.value = true
        const response = await dietsService.assignDietToPatient(
          props.dietType,
          props.dietId,
          patient.value.id,
        )
        if (response.status === 201) {
          await router.push({
            name: 'DietDetailWeek',
            params: {
              dietId: response.data.id,
              dietType: 'patient',
            },
          })
          router.go(0)
        }
      } catch (error) {
        reportError(error, 'Error during assigning diet to patient', {
          dietId: props.dietId,
          dietType: props.dietType,
          patientId: patient.value.id,
        })
        throw error
      } finally {
        sending.value = false
        close()
      }
    }
  }
  const assignToPatientRedirect = () => {
    location.href = `/patients/`
  }
  const openPatientProfile = () => {
    location.href = `/patients/${patient.value?.id}/settings/`
  }
</script>

<style>
  .diet-assign-to-patient-modal {
    width: 550px;
    overflow: visible !important;
  }
</style>

<style scoped>
  .label {
    font-size: 10px;
    color: var(--color-tertiary-text);
  }

  .patient-week {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .patient {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
  }

  .patient__select {
    display: flex;
    flex-direction: row;
  }

  .info-text {
    margin-top: 8px;
    font-size: 12px;
    color: var(--color-tertiary-text);
  }

  .badge {
    position: relative;
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 4px;
    font-size: 12px;
    font-weight: 500;
    color: #b54708;
    background-color: #fffaeb;
    border: 1px solid;
    border-color: #fedf89;
    border-radius: 6px;
  }

  .icon-warning {
    color: #b54708;
  }

  .patient-option {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    min-width: 0;
    padding: 2px 0;
  }

  .patient-option__name {
    display: flex;
    gap: 6px;
    align-items: center;
    font-weight: 500;
  }

  .patient-option__email {
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--color-tertiary-text);
    white-space: nowrap;
  }

  .patient-option__warning {
    flex-shrink: 0;
    font-size: 14px;
    color: #b54708;
  }
</style>
